import React, { FC } from 'react'
import { Box, Typography, Divider } from '@mui/material'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { SoapImprovementCategory } from '@/apps/legacy/features/formatter/types/soapImprovement'

// 空状態のスタイル定数
const EMPTY_STATE_STYLES = {
  icon: {
    color: colorTokens.border.medium,
    marginBottom: 0.25,
  },
  container: {
    backgroundColor: colorTokens.background.subtle,
    border: `1px dashed ${colorTokens.border.light}`,
    borderRadius: 2,
    py: 1.5,
    px: 2.5,
    gap: 0.5,
  },
  message: {
    color: colorTokens.text.secondary,
    lineHeight: 1.4,
  },
  chatGuide: {
    divider: {
      marginTop: 1,
      paddingTop: 1,
      borderColor: colorTokens.grey[200],
    },
    icon: {
      color: colorTokens.grey[500],
    },
    text: {
      color: colorTokens.grey[500],
    },
  },
} as const

// 空状態の設定
const EMPTY_STATE_CONFIG = {
  basic: {
    message: '基本項目は適切に記述されていると思われます',
  },
  kasan: {
    message: '該当する加算項目は検知されませんでした',
  },
  followup: {
    message: 'フォローアップは適切に記述されていると思われます',
  },
} as const

interface ActionCategorySectionProps {
  category: SoapImprovementCategory
  children: React.ReactNode
  isEmpty?: boolean
}

export const ActionCategorySection: FC<ActionCategorySectionProps> = ({
  category,
  children,
  isEmpty = false,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* カテゴリヘッダー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          px: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: category.color,
          }}
        >
          {category.label}
        </Typography>
        <Divider
          sx={{
            flex: 1,
            ml: 1,
            borderColor: category.color,
            opacity: 0.3,
          }}
        />
      </Box>

      {/* カテゴリコンテンツ */}
      <Box sx={{ pl: 0.5, pr: 0.5 }}>
        {isEmpty ? (
          <Box
            sx={{
              ...EMPTY_STATE_STYLES.container,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                color: EMPTY_STATE_STYLES.icon.color,
                mb: EMPTY_STATE_STYLES.icon.marginBottom,
              }}
            />
            <Typography sx={EMPTY_STATE_STYLES.message}>
              {
                EMPTY_STATE_CONFIG[
                  category.id as keyof typeof EMPTY_STATE_CONFIG
                ]?.message
              }
            </Typography>
            {/* SOAP更新への誘導 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                mt: EMPTY_STATE_STYLES.chatGuide.divider.marginTop,
                pt: EMPTY_STATE_STYLES.chatGuide.divider.paddingTop,
                borderTop: `1px solid ${EMPTY_STATE_STYLES.chatGuide.divider.borderColor}`,
              }}
            >
              <ChatBubbleOutlineIcon sx={EMPTY_STATE_STYLES.chatGuide.icon} />
              <Typography sx={EMPTY_STATE_STYLES.chatGuide.text}>
                個別の更新は右上の...ボタンから
              </Typography>
            </Box>
          </Box>
        ) : (
          children
        )}
      </Box>
    </Box>
  )
}
